/* eslint-disable react/no-find-dom-node */
/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { omit } from 'lodash';
import { classnames as cx, FlexLayout, FlexItem, SVGIcon, Container } from '@waca/ca-ui-toolkit';

import './TextIcon.scss';

const FlexElement = ({
	tid,
	children,
	classNames,
	...others
}: {
	tid?: string;
	children?: any;
	className?: string;
	[key: string]: any;
}) => (
	<FlexItem className={classNames} textAlign="center" data-tid={tid} {...others}>
		{children}
	</FlexItem>
);

FlexElement.propTypes = {
	tid: PropTypes.string,
	children: PropTypes.node,
	classNames: PropTypes.string,
	gutter: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
};

export default class TextIcon extends Component<any, any> {
	static Header = props => (
		<FlexElement gutter={[0, 0, 1, 0]} classNames="text-icon__header" {...props} />
	);

	static SubHeader = props => (
		<FlexElement gutter={[0, 0, 1, 0]} classNames="text-icon__sub_header" {...props} />
	);

	static Body = props => <FlexElement classNames="text-icon__body" {...props} />;

	static Icon = ({ icon, gutter = [], ...other }) => (
		<FlexElement gutter={gutter}>
			<SVGIcon className="text-icon__icon" iconId={icon && icon.id} {...other} />
		</FlexElement>
	);

	static propTypes = {
		className: PropTypes.string,
		onSelect: PropTypes.func,
		onConfirm: PropTypes.func,
		isSelected: PropTypes.bool,
		children: PropTypes.node,
		onClick: PropTypes.func,
		onDoubleClick: PropTypes.func,
		onKeyDown: PropTypes.func,
	};

	focus() {
		const containerDomNode = findDOMNode(this._container) as HTMLElement;

		containerDomNode && containerDomNode.focus();
	}

	_container: Component;

	_onClick = () => {
		const { onClick, onSelect } = this.props;

		onSelect && onSelect();

		onClick && onClick();
	};

	_onDoubleClick = () => {
		const { onDoubleClick, onConfirm } = this.props;

		onConfirm && onConfirm();

		onDoubleClick && onDoubleClick();
	};

	_onKeyDown = e => {
		const { onSelect, onKeyDown } = this.props;

		if (e.keyCode === 32 || e.keyCode === 13) {
			onSelect && onSelect();
		}

		onKeyDown && onKeyDown(e);
	};

	_isClickable = () => this.props.onSelect || this.props.onClick || this.props.onDoubleClick;

	render() {
		const { className = '', isSelected, children, ...other } = this.props;

		const passThroughProps = omit(other, 'onSelect', 'onConfirm');
		const isClickable = this._isClickable();

		return (
			<Container
				className={cx('text-icon__button', {
					clickable: isClickable,
					selected: isSelected,
				})}
				tabIndex={0}
				role={isClickable ? 'button' : null}
				{...passThroughProps}
				onClick={this._onClick}
				onDoubleClick={this._onDoubleClick}
				onKeyDown={this._onKeyDown}
				ref={node => (this._container = node)}
			>
				<FlexLayout
					justifyContent="center"
					alignItems="center"
					direction="column"
					fullHeight
					className={`text-icon ${className}`}
				>
					{children}
				</FlexLayout>
			</Container>
		);
	}
}
